/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode, type PropsWithChildren, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router';
import { Link } from '@/components/link';
import Search from './search';
import CartBadge from './cart-badge';
import UserActions from './user-actions/user-actions';
import { useTranslation } from 'react-i18next';
import logo from '/images/logo.svg';
import { Button } from '@/components/ui/button';
import { SparklesIcon } from '@/components/icons';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { openAgentWidget, isCimulateEnabled, validateCimulateConfig } from '@/components/cimulate';
import { UITarget } from '@/targets/ui-target';
import { Component } from '@/lib/decorators/component';
import { RegionDefinition } from '@/lib/decorators';

@Component('header', {
    name: 'Header',
    group: 'Layout',
    description: 'Global site header with navigation, search, and cart',
    embedded: true,
    component_id: 'header',
})
@RegionDefinition([
    { id: 'announcement', name: 'Announcement', description: 'Displayed below the main menu' },
    {
        id: 'navigation',
        name: 'Navigation',
        description: 'Main menu: add one Main Navigation block. When empty, the catalog categories are shown.',
        maxComponents: 1,
        componentTypeInclusions: ['priceshoes_layout.mainNavigation'],
    },
])
// oxlint-disable-next-line react/only-export-components -- oxlint flags the co-exported Page Designer metadata class; eslint-plugin-react-refresh does not
export class HeaderMetadata {}

interface HeaderProps extends PropsWithChildren {
    beforeHeader?: ReactNode;
    /**
     * Slot rendered right after the sticky `<header>` (below the main menu). Used for the
     * pre-header announcement carousel; it lives outside the sticky element so it scrolls away
     * with the page instead of staying pinned.
     */
    announcementSlot?: ReactNode;
    variant?: 'full' | 'checkout';
}

function HeaderLogo({ alt }: { alt: string }): ReactElement {
    return (
        <Link to="/" className="flex-shrink-0 flex items-center" data-testid="header-logo">
            <img
                src={logo}
                alt={alt}
                width={71}
                height={40}
                fetchPriority="high"
                className="h-[47px] w-20 object-cover lg:h-10 lg:w-[71px] lg:object-contain [filter:var(--header-logo-filter)]"
            />
        </Link>
    );
}

// Isolates the `useLocation()` subscription so that route changes only re-render the Search component (via key reset)
// without cascading a re-render through the entire `Header` tree, which would unnecessarily re-render the navigation
// menu and other stable children.
function LocationKeyedSearch() {
    const location = useLocation();
    return <Search key={`${location.pathname}${location.search}`} />;
}

export default function Header({
    children,
    beforeHeader,
    announcementSlot,
    variant = 'full',
}: HeaderProps): ReactElement {
    const { t } = useTranslation('header');
    const headerRef = useRef<HTMLElement>(null);
    const config = useConfig();
    const showChat =
        variant === 'full' &&
        isCimulateEnabled(config.cimulateAgent?.enabled) &&
        validateCimulateConfig(config.cimulateAgent);
    const updateHeaderHeight = useCallback(() => {
        if (headerRef.current) {
            const height = `${headerRef.current.offsetHeight}px`;
            headerRef.current.style.setProperty('--header-height', height);
            document.documentElement.style.setProperty('--header-height', height);
        }
    }, []);

    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        updateHeaderHeight();
        const observer = new ResizeObserver(updateHeaderHeight);
        observer.observe(el);
        return () => observer.disconnect();
    }, [updateHeaderHeight]);

    if (variant === 'checkout') {
        return (
            <header
                ref={headerRef}
                className="bg-header-background text-header-foreground sticky top-0 z-50 [@media(max-height:400px)]:static">
                <div className="section-container">
                    <div className="flex items-center h-16">
                        <HeaderLogo alt={t('logoAlt')} />
                        <div className="flex-1" />
                        <CartBadge />
                    </div>
                </div>
            </header>
        );
    }

    return (
        <>
            <header
                ref={headerRef}
                className="bg-header-background text-header-foreground sticky top-0 z-50 [@media(max-height:400px)]:static">
                <div className="flex justify-end section-container">{beforeHeader}</div>
                <div className="max-w-screen-2xl mx-auto">
                    {/* Top row. Mobile: hamburger | centered logo | actions. Desktop: logo + search left, actions right */}
                    <div className="grid grid-cols-[1fr_auto_1fr] items-center border-b border-header-mobile-divider px-4 py-2 sm:px-8 lg:flex lg:justify-between lg:gap-x-8 lg:border-b-0 lg:py-3">
                        {/* Hamburger - mobile only */}
                        <div className="flex items-center lg:hidden">{children}</div>

                        <div className="flex min-w-0 items-center gap-8 lg:flex-1">
                            <HeaderLogo alt={t('logoAlt')} />

                            {/* Search - desktop only */}
                            <div
                                className="hidden lg:block min-w-0 flex-1 max-w-[760px]"
                                data-testid="header-search-desktop">
                                <LocationKeyedSearch />
                            </div>
                        </div>

                        <div className="flex shrink-0 items-center justify-self-end lg:gap-2">
                            <UITarget targetId="sfcc.header.before.cart" />
                            {showChat && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="cursor-pointer lg:px-4 px-1 text-header-foreground hover:bg-transparent hover:opacity-50 transition-opacity"
                                    onClick={() => openAgentWidget()}
                                    aria-label={t('openChat')}>
                                    <SparklesIcon />
                                </Button>
                            )}
                            <UserActions />
                            <CartBadge />
                        </div>
                    </div>

                    {/* Mobile search - second row */}
                    <div className="px-4 py-2 sm:px-8 lg:hidden" data-testid="header-search-mobile">
                        <LocationKeyedSearch />
                    </div>
                    <div className="px-4 sm:px-8">
                        <UITarget targetId="sfcc.header.bnpl.banner" />
                    </div>
                </div>

                {/* Navigation Menu - desktop only. */}
                <div className="hidden lg:block border-t-[0.5px] border-header-nav-border">
                    <div className="max-w-screen-2xl mx-auto px-8 py-3 flex min-w-0 items-center">{children}</div>
                </div>
            </header>
            {announcementSlot}
        </>
    );
}
